import {
    ChurnReportData,
    fetchSubscriptionChurnReport,
    setSorting,
} from 'features/reports/subscriptionChurnReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useCurrentCountry } from 'hooks/miscHooks'
import { useCallback, useMemo } from 'react'
import ReportList, { ColumnDefinition } from './common/ReportList'
import { renderChurnReason } from './common/reportRenderers'
import {
    renderCurrency,
    renderDate,
    renderProspectValue,
    renderProviderId,
    renderProviderStatus,
    renderSubscriptionStatus,
    renderText,
} from './common/reportRenderers'

const SubscriptionChurnReportList = () => {
    const dispatch = useAppDispatch()
    const country = useCurrentCountry()

    // Select data from Redux store
    const { pages, rows, pagination, filters, sorting, isLoading } = useAppSelector(
        (state) => state.subscriptionChurnReport,
    )

    // No need for RTK Query hook - we'll use dispatch directly

    // Memoized month names calculation
    const monthNames = useMemo(() => {
        const now = new Date()
        const thirdMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1)
        const secondMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const previousMonth = new Date(now.getFullYear(), now.getMonth(), 1)

        return {
            third: thirdMonth.toLocaleString('default', { month: 'long' }),
            second: secondMonth.toLocaleString('default', { month: 'long' }),
            previous: previousMonth.toLocaleString('default', { month: 'long' }),
        }
    }, [])

    // Memoized columns definition
    const columns = useMemo(
        (): ColumnDefinition[] => [
            { key: 'provider_id', name: 'Provider ID' },
            { key: 'provider_status', name: 'Provider Status' },
            { key: 'subscription_status', name: 'Subscription Status' },
            { key: 'subscription_start_date', name: 'Subscription Start Date' },
            { key: 'subscription_end_date', name: 'Subscription End Date' },
            { key: 'churn_date', name: 'Churn Date' },
            { key: 'churn_reason', name: 'Churn Reason' },
            {
                key: 'invoice_amount_third_month',
                name: `Invoice Amount ${monthNames.third} (€)`,
                sortable: false,
            },
            {
                key: 'invoice_amount_second_month',
                name: `Invoice Amount ${monthNames.second} (€)`,
                sortable: false,
            },
            {
                key: 'invoice_amount_previous_month',
                name: `Invoice Amount ${monthNames.previous} (€)`,
                sortable: false,
            },
            { key: 'country_code', name: 'Country Code' },
            { key: 'branch', name: 'Branch' },
            { key: 'service_id', name: 'Service ID' },
            {
                key: 'first_invoice_subscription_month',
                name: 'First Invoice Subscription Month',
                sortable: false,
            },
            { key: 'prospect_value', name: 'Prospect Value', sortable: false },
            { key: 'subscription_price', name: 'Subscription Price (€)', sortable: false },
            { key: 'discount_total', name: 'Discount Total (€)', sortable: false },
        ],
        [monthNames],
    )

    // Memoized row renderer
    const renderRow = useCallback(
        (id: string) => {
            const item = rows[id] as unknown as ChurnReportData
            if (!item) {
                return { cols: [] }
            }

            return {
                cols: [
                    renderProviderId(
                        item.provider_id,
                        'provider_id',
                        '/service/providers/provider',
                    ),
                    renderProviderStatus(item.provider_status, 'provider_status'),
                    renderSubscriptionStatus(item.subscription_status, 'subscription_status'),
                    renderDate(item.subscription_start_date, 'subscription_start_date'),
                    renderDate(item.subscription_end_date, 'subscription_end_date'),
                    renderDate(item.churn_date, 'churn_date'),
                    renderChurnReason(item.churn_reason, 'churn_reason'),
                    renderCurrency(item.invoice_amount_third_month, 'invoice_amount_third_month'),
                    renderCurrency(item.invoice_amount_second_month, 'invoice_amount_second_month'),
                    renderCurrency(
                        item.invoice_amount_previous_month,
                        'invoice_amount_previous_month',
                    ),
                    renderText(item.country_code, 'country_code'),
                    renderText(item.branch, 'branch'),
                    renderText(item.service_id?.toString(), 'service_id'),
                    renderText(
                        item.first_invoice_subscription_month,
                        'first_invoice_subscription_month',
                    ),
                    renderProspectValue(item.prospect_value, 'prospect_value'),
                    renderCurrency(item.subscription_price, 'subscription_price'),
                    renderCurrency(item.discount_total, 'discount_total'),
                ],
            }
        },
        [rows],
    )

    // Handle page change
    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(fetchSubscriptionChurnReport({ filters, page, sorting, country }))
        },
        [dispatch, filters, sorting, country],
    )

    // Handle sort change
    const handleSortChange = useCallback(
        (field: string, direction: 'ASC' | 'DESC') => {
            const newSorting = { field, direction }
            // Update Redux state for sorting
            dispatch(setSorting(newSorting))
            dispatch(
                fetchSubscriptionChurnReport({ filters, page: 0, sorting: newSorting, country }),
            )
        },
        [dispatch, filters, country],
    )

    return (
        <ReportList
            pages={pages}
            rows={rows}
            isLoading={isLoading}
            pagination={pagination}
            filters={filters}
            fetchData={handlePageChange}
            columns={columns}
            renderRow={renderRow}
            sorting={sorting}
            onSortChange={handleSortChange}
            emptyMessage="No churn data found for the selected filters."
            loadingMessage="Loading churn data..."
        />
    )
}

export default SubscriptionChurnReportList
